import { useEffect, useState } from "react";
import { useContent } from "@/lib/content-context";
import { fileUrl } from "@/lib/api";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { MapPin, Clock, Phone, Search, X, ArrowRight, CheckCircle2, Star } from "lucide-react";

// Parse hours like "8 AM — 10 PM" / "8 AM \u2014 11 PM" / "9 AM — 10:30 PM"
function parseHours(hoursStr) {
    if (!hoursStr) return null;
    const m = hoursStr.match(/(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)\s*[—\u2014\-]+\s*(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)/i);
    if (!m) return null;
    const to24 = (h, ap) => {
        h = parseInt(h, 10);
        if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
        if (ap.toUpperCase() === "AM" && h === 12) h = 0;
        return h;
    };
    return {
        openH: to24(m[1], m[3]),
        openM: parseInt(m[2] || "0", 10),
        closeH: to24(m[4], m[6]),
        closeM: parseInt(m[5] || "0", 10),
    };
}

function nowIST() {
    const fmt = new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
    const parts = fmt.formatToParts(new Date());
    const h = parseInt(parts.find((p) => p.type === "hour").value, 10);
    const m = parseInt(parts.find((p) => p.type === "minute").value, 10);
    return h * 60 + m;
}

function isOpenNow(hoursStr) {
    const parsed = parseHours(hoursStr);
    if (!parsed) return null;
    const now = nowIST();
    const open = parsed.openH * 60 + parsed.openM;
    let close = parsed.closeH * 60 + parsed.closeM;
    if (close <= open) close += 24 * 60; // crosses midnight
    return now >= open && now < close;
}

const ALL_TAGS = ["Airport", "Dine-In", "Work-Friendly", "Quick Grab", "Cosy Corner", "Flagship"];

export default function OurCafes() {
    const { content } = useContent();
    const [search, setSearch] = useState("");
    const [activeTag, setActiveTag] = useState("All");
    const [tick, setTick] = useState(0); // re-eval open/closed every minute

    useEffect(() => {
        const i = setInterval(() => setTick((t) => t + 1), 60000);
        return () => clearInterval(i);
    }, []);

    if (!content) return null;
    const oc = content.our_cafes || {};
    const allCafes = (oc.cafes || []).filter((c) => c.active !== false);
    const expanding = oc.expanding || {};

    const q = search.trim().toLowerCase();
    const filtered = allCafes.filter((c) => {
        if (activeTag !== "All" && !(c.vibe_tags || []).includes(activeTag)) return false;
        if (!q) return true;
        return [c.name, c.area, c.character_note, ...(c.vibe_tags || [])]
            .some((v) => (v || "").toLowerCase().includes(q));
    });
    // tick keeps live status re-evaluated; suppress unused warning
    void tick;

    return (
        <div data-testid="our-cafes-page">
            <SEO title="Our Cafes" description={oc.subtitle} image={fileUrl(oc.hero_image)} path="/our-cafes" />

            {/* HERO */}
            <section
                className="relative w-full flex items-center justify-center text-center px-6 pt-28 md:pt-32"
                style={{ minHeight: "50vh" }}
                data-testid="cafes-hero"
            >
                <div className="absolute inset-0 overflow-hidden md:min-h-[60vh]" style={{ minHeight: "50vh" }}>
                    <img src={fileUrl(oc.hero_image)} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "rgba(26,15,0,0.4)" }} />
                </div>
                <div className="relative z-10 max-w-3xl py-16 md:py-24">
                    <Reveal>
                        <div className="tracked text-gold text-[10px] mb-6">{oc.label}</div>
                        <h1
                            className="font-editorial font-bold text-cream leading-[0.98] mb-6"
                            style={{ fontSize: "clamp(2.25rem, 6vw, 4rem)" }}
                            data-testid="cafes-title"
                        >
                            {oc.title}
                        </h1>
                        <p className="font-body text-cream/85 max-w-xl mx-auto text-base md:text-lg leading-[1.7]">
                            {oc.subtitle}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 1 — ALL LOCATIONS */}
            <section className="px-5 sm:px-8 md:px-12 pt-20 md:pt-24 pb-24 md:pb-28" style={{ backgroundColor: "#faf6ef" }} data-testid="cafes-grid-section">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
                            <div>
                                <div className="tracked text-coffee/50 text-[10px] mb-3">All Locations</div>
                                <h2 className="font-editorial font-bold text-coffee leading-[1.05]" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)" }}>
                                    Hideout — ready for you
                                </h2>
                            </div>
                            <div className="relative w-full md:w-auto md:min-w-[300px]">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-coffee/40" />
                                <input
                                    type="text"
                                    placeholder="Search outlet or area…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    data-testid="cafes-search"
                                    className="w-full pl-9 pr-9 py-3 text-sm bg-white border border-coffee/15 focus:border-coffee outline-none font-body text-coffee"
                                />
                                {search && (
                                    <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-coffee/50 hover:text-coffee" aria-label="clear">
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </Reveal>

                    {/* Vibe tag pills */}
                    <Reveal delay={0.05}>
                        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-10 scrollbar-hide" data-testid="cafes-tag-filter">
                            {["All", ...ALL_TAGS].map((tag) => {
                                const isActive = activeTag === tag;
                                return (
                                    <button
                                        key={tag}
                                        onClick={() => setActiveTag(tag)}
                                        data-testid={`tag-${tag.toLowerCase().replace(/\W+/g, "-")}`}
                                        className={`whitespace-nowrap tracked text-[10px] px-4 py-2.5 transition-all duration-300 ${
                                            isActive
                                                ? "bg-coffee text-gold border border-coffee"
                                                : "bg-transparent text-coffee/70 border border-coffee/20 hover:border-coffee"
                                        }`}
                                    >
                                        {tag}
                                    </button>
                                );
                            })}
                        </div>
                    </Reveal>

                    {/* Masonry grid (CSS columns) */}
                    {filtered.length === 0 ? (
                        <div className="text-center py-20 font-editorial text-coffee/40 text-2xl">
                            No cafes match. Try another filter.
                        </div>
                    ) : (
                        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-7" data-testid="cafes-grid">
                            {filtered.map((c, i) => (
                                <CafeCard key={c.id || i} cafe={c} idx={i} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* SECTION 3 — EXPANDING SOON */}
            <ExpandingSection data={expanding} />
        </div>
    );
}

function CafeCard({ cafe, idx }) {
    const open = isOpenNow(cafe.hours);
    const aspect = cafe.featured ? "aspect-video" : "aspect-[4/3]";
    return (
        <div
            id={`cafe-${cafe.id || idx}`}
            data-testid={`cafe-card-${cafe.id || idx}`}
            className={`break-inside-avoid mb-5 md:mb-7 group ${cafe.featured ? "bg-white" : "bg-white"}`}
            style={cafe.featured ? { border: "1px solid rgba(200,169,110,0.6)", boxShadow: "0 30px 70px -40px rgba(184,141,82,0.35)" } : { border: "1px solid rgba(26,15,0,0.08)" }}
        >
            {/* Image */}
            <div className={`relative ${aspect} overflow-hidden bg-coffee/5`}>
                <img
                    src={fileUrl(cafe.image)}
                    alt={cafe.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                />
                {/* Open / Closed badge */}
                {open !== null && (
                    <div className="absolute top-3 right-3" data-testid={`cafe-status-${cafe.id || idx}`}>
                        <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 backdrop-blur-md font-mono tracked text-[9px]"
                            style={{
                                background: open ? "rgba(26,15,0,0.85)" : "rgba(245,230,200,0.92)",
                                color: open ? "#c8a96e" : "#1a0f00",
                                border: open ? "1px solid rgba(200,169,110,0.5)" : "1px solid rgba(26,15,0,0.2)",
                            }}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-gold animate-pulse" : "bg-coffee/40"}`} />
                            {open ? "OPEN NOW" : "CLOSED"}
                        </span>
                    </div>
                )}
                {/* Featured badge */}
                {cafe.featured && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1.5 bg-gold tracked text-[9px] text-coffee" data-testid={`featured-${cafe.id || idx}`}>
                        <Star size={10} className="fill-coffee" /> FEATURED
                    </div>
                )}
            </div>

            {/* Body */}
            <div className="p-5 md:p-6">
                {/* Vibe tags */}
                {cafe.vibe_tags && cafe.vibe_tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                        {cafe.vibe_tags.map((t) => (
                            <span key={t} className="tracked text-[9px] px-2 py-1 bg-coffee text-gold">{t}</span>
                        ))}
                    </div>
                )}
                <h3 className="font-editorial font-bold text-coffee text-xl md:text-[22px] leading-[1.18] mb-2">{cafe.name}</h3>
                <div className="font-body text-coffee/60 text-sm mb-4 flex items-center gap-2">
                    <MapPin size={13} className="text-gold flex-shrink-0" /> {cafe.area}
                </div>
                {cafe.character_note && (
                    <p className="font-body italic text-coffee/75 text-[15px] leading-[1.65] mb-5" style={{ fontFamily: '"Open Sans", "Helvetica Neue", sans-serif' }}>
                        "{cafe.character_note}"
                    </p>
                )}
                <div className="flex items-center gap-2 font-mono text-[10px] tracked text-coffee/65 mb-5">
                    <Clock size={11} className="text-gold" /> {cafe.hours}
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2 pt-4" style={{ borderTop: "1px solid rgba(26,15,0,0.08)" }}>
                    {cafe.maps_url && (
                        <a
                            href={cafe.maps_url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`directions-${cafe.id || idx}`}
                            className="inline-flex items-center justify-center gap-1.5 tracked text-[10px] bg-coffee text-cream hover:bg-gold hover:text-coffee transition-colors duration-300"
                            style={{ minHeight: 48 }}
                        >
                            <MapPin size={12} /> Directions
                        </a>
                    )}
                    {cafe.phone && (
                        <a
                            href={`tel:${cafe.phone.replace(/\s+/g, "")}`}
                            data-testid={`call-${cafe.id || idx}`}
                            className="inline-flex items-center justify-center gap-1.5 tracked text-[10px] bg-transparent text-coffee hover:bg-coffee hover:text-cream transition-colors duration-300"
                            style={{ minHeight: 48, border: "1px solid rgba(26,15,0,0.25)" }}
                        >
                            <Phone size={12} /> Call Us
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

function ExpandingSection({ data }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [city, setCity] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            await api.post("/notify-me", { email, city });
            setDone(true);
            setEmail(""); setCity("");
        } catch {
            toast.error("Couldn't save. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className="relative px-5 sm:px-8 md:px-12 py-24 md:py-32"
            style={{ backgroundColor: "#c8a96e" }}
            data-testid="cafes-expanding"
        >
            <div className="max-w-3xl mx-auto text-center">
                <Reveal>
                    <h2 className="font-editorial font-bold text-coffee leading-[1.05] mb-5" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                        {data.heading}
                    </h2>
                    <p className="font-body text-coffee/80 text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-10">
                        {data.body}
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
                        <Link
                            to={data.primary_link || "/partner"}
                            data-testid="cafes-partner-cta"
                            className="inline-flex items-center justify-center gap-2 tracked text-[10px] px-6 bg-coffee text-gold hover:bg-coffee-dark transition-colors duration-300"
                            style={{ minHeight: 48 }}
                        >
                            {data.primary_label || "Partner With Us"} <ArrowRight size={14} />
                        </Link>
                        <button
                            onClick={() => { setDone(false); setModalOpen(true); }}
                            data-testid="cafes-notify-cta"
                            className="inline-flex items-center justify-center gap-2 tracked text-[10px] px-6 bg-transparent text-coffee hover:bg-coffee/10 transition-colors duration-300"
                            style={{ minHeight: 48, border: "1px solid rgba(26,15,0,0.5)" }}
                        >
                            {data.secondary_label || "Notify Me"} <ArrowRight size={14} />
                        </button>
                    </div>
                </Reveal>
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="notify-modal" onClick={() => setModalOpen(false)}>
                    <div className="absolute inset-0" style={{ background: "rgba(26,15,0,0.7)", backdropFilter: "blur(4px)" }} />
                    <div className="relative bg-white max-w-md w-full p-7 md:p-9" style={{ border: "1px solid rgba(200,169,110,0.5)" }} onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 text-coffee/50 hover:text-coffee" aria-label="close" data-testid="notify-close">
                            <X size={20} />
                        </button>
                        {done ? (
                            <div className="text-center py-4">
                                <CheckCircle2 size={36} strokeWidth={1.2} className="text-gold mx-auto mb-5" />
                                <h3 className="font-editorial font-bold text-coffee text-2xl md:text-3xl mb-3">You're on the list.</h3>
                                <p className="font-body text-coffee/65 text-sm">We'll write the moment we land near you.</p>
                                <button onClick={() => setModalOpen(false)} className="btn-pill btn-pill-coffee mt-8" data-testid="notify-done-close">Close</button>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="notify-form">
                                <div className="tracked text-coffee/50 text-[10px] mb-3">Notify Me</div>
                                <h3 className="font-editorial font-bold text-coffee text-2xl md:text-3xl mb-2">Tell us where you'd love a Hideout.</h3>
                                <p className="font-body text-coffee/60 text-sm mb-6">We'll let you know the moment doors open.</p>
                                <label className="tracked text-coffee/60 text-[10px] block mb-2">Email *</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="notify-email"
                                    className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none mb-5"
                                />
                                <label className="tracked text-coffee/60 text-[10px] block mb-2">City</label>
                                <input
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Patna, Ranchi, Gaya…"
                                    data-testid="notify-city"
                                    className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none mb-8"
                                />
                                <button type="submit" disabled={submitting} className="btn-pill btn-pill-coffee w-full justify-center disabled:opacity-50" style={{ background: "#1a0f00", color: "#c8a96e", borderColor: "#1a0f00" }} data-testid="notify-submit">
                                    {submitting ? "Saving…" : "Keep Me Posted"} <ArrowRight size={14} />
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
